import React, { useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useScanCadence } from '@/hooks/useScanCadence';
import { useScanSchedule } from '@/hooks/useScanSchedule';
import {
  buildAnyTimeSentence,
  buildSentence,
  DEFAULT_SENTENCE,
} from '@/sections/settings/components/forms/scanScheduleSentence';
import { SentenceText } from '@/sections/settings/components/forms/SentenceText';
import { ScanScheduleWizard } from '@/sections/settings/components/modals/ScanScheduleWizard';
import { SettingCompactCard } from '@/sections/settings/components/primitives/SettingCompactCard';
import { cn } from '@/utils/classname';

const SCAN_SCHEDULE_LOCK_TOOLTIP =
  'Contact your Praetorian account team to update this setting.';

export interface ScanScheduleCardProps {
  /** Email for impersonation - if provided, fetches/writes for this user. */
  email?: string;
  /** Whether edit is enabled at all (default true). Combined with super_admin gate. */
  editable?: boolean;
}

/**
 * Merged "Scan Schedule" card (collapsed) — FIDELITY §A/§B.
 *
 * Replaces today's standalone "Schedule" card at position #5 in /settings/scan.
 * States the full truth (cadence + window) in one sentence (§E). Edit is gated
 * on the super_admin entitlement (`canEdit = editable && isSuperAdmin`) and opens
 * a 2-step wizard. Non-super-admins see a disabled Edit button (locked tooltip)
 * and CANNOT open the wizard.
 */
export const ScanScheduleCard: React.FC<ScanScheduleCardProps> = ({
  email,
  editable = true,
}) => {
  const { isSuperAdmin } = useEntitlements();
  const [wizardOpen, setWizardOpen] = useState(false);

  const { currentCadence, isSet: cadenceIsSet } = useScanCadence({ email });
  const { currentScanSchedule } = useScanSchedule({ email });

  // Super-admin-only edit for the whole card: the scan schedule is a cost-control
  // setting gated on the super_admin entitlement (matches AISpendCard).
  const canEdit = editable && isSuperAdmin;

  // A window is simply ≥1 selected day (matching the backend Schedule.HasWindow):
  // start==end (or absent) is an all-day window, not the absence of one. Cadence
  // and window are orthogonal — a tenant may have a cadence with no window
  // ("Any time"), both, or neither.
  const hasWindow = (currentScanSchedule?.days?.length ?? 0) > 0;
  // An all-day window has no time bound (start missing or equal to end).
  const allDay =
    !currentScanSchedule?.start ||
    !currentScanSchedule?.end ||
    currentScanSchedule.start === currentScanSchedule.end;
  // "Set" means a cadence is configured — the window is optional.
  const isSet = cadenceIsSet;

  let sentence = DEFAULT_SENTENCE;
  if (isSet && hasWindow && currentScanSchedule) {
    sentence = buildSentence({
      frequency: currentCadence.frequency,
      days: currentScanSchedule.days,
      start: currentScanSchedule.start ?? '',
      end: currentScanSchedule.end ?? '',
      dayOfMonth: currentCadence.dayOfMonth,
      allDay,
    });
  } else if (isSet) {
    // Cadence-only ("Any time"): recurrence with no launch window.
    sentence = buildAnyTimeSentence(
      currentCadence.frequency,
      currentCadence.dayOfMonth
    );
  }

  return (
    <>
      <SettingCompactCard data-testid="scan-schedule-card">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <h4 className="text-chariot-text-primary font-bold">
              Scan Schedule
            </h4>
            {!isSet && (
              <span className="bg-chariot-layer2 text-chariot-status-info rounded-full px-2 py-0.5 text-xs font-bold tracking-wide uppercase">
                Default
              </span>
            )}
          </div>
          <SentenceText
            parts={sentence}
            data-testid="scan-schedule-card-sentence"
            className={cn(
              'text-sm',
              isSet
                ? 'text-chariot-text-primary'
                : 'text-chariot-text-secondary'
            )}
          />
          <p className="text-chariot-text-secondary text-xs">
            How often base scans recur, and the day-of-week window when they may
            launch (UTC).
          </p>
        </div>

        {canEdit ? (
          <div className="absolute top-1/2 right-4 -translate-y-1/2">
            <Button
              onClick={() => setWizardOpen(true)}
              variant="ghost"
              size="sm"
              data-testid="scan-schedule-edit-button"
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="absolute top-1/2 right-4 -translate-y-1/2">
            <Tooltip title={SCAN_SCHEDULE_LOCK_TOOLTIP}>
              {/* span wrapper enables hover/focus events on disabled button for tooltip */}
              <span tabIndex={0} data-testid="scan-schedule-edit-locked">
                <Button variant="ghost" size="sm" disabled>
                  Edit
                </Button>
              </span>
            </Tooltip>
          </div>
        )}
      </SettingCompactCard>

      {canEdit && wizardOpen && (
        <ScanScheduleWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          email={email}
          initialFrequency={currentCadence.frequency}
          initialDayOfMonth={currentCadence.dayOfMonth}
          initialManualBypass={currentScanSchedule?.manualBypass ?? false}
          initialAnyTime={!hasWindow}
        />
      )}
    </>
  );
};
